import { useQuery } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

import { queries } from '../common/api/queryKeys/queries';
import { useRole } from '../common/hooks/useRole';

import { SupplierSubscriptionPlansModal } from './components/SupplierSubscriptionPlansModal';
import { SupplierSubscriptionPlansContext } from './SupplierSubscriptionPlansContext';
import { useSupplierSubscriptionReleaseFlags } from './useSupplierSubscriptionReleaseFlags';

interface SupplierSubscriptionPlansProviderProps {
    children: ReactNode;
}

export const SupplierSubscriptionPlansProvider = ({ children }: SupplierSubscriptionPlansProviderProps) => {
    const role = useRole();
    const [isOpen, setIsOpen] = useState(false);
    const supplierGroupId = role.group?.id;
    const isSupplierGroupAdmin = !!supplierGroupId && !!role.hasAnyAdminRights();
    const releaseFlags = useSupplierSubscriptionReleaseFlags(isSupplierGroupAdmin);
    const canViewPlans = isSupplierGroupAdmin && releaseFlags.earlySubscriptionSignup;
    const subscription = useQuery({
        ...queries.supplierGroups.detail(supplierGroupId ?? '')._ctx.subscriptionPlan,
        enabled: canViewPlans,
        throwOnError: false,
    });

    return (
        <SupplierSubscriptionPlansContext.Provider
            value={{
                currentPlan: subscription.data?.plan,
                canViewPlans,
                isFeeEnforced: releaseFlags.feeEnforcement,
                openPlans: () => setIsOpen(true),
            }}
        >
            {children}
            {canViewPlans ? (
                <SupplierSubscriptionPlansModal
                    isOpen={isOpen}
                    onDismiss={() => setIsOpen(false)}
                    supplierGroupId={role.group.id}
                />
            ) : null}
        </SupplierSubscriptionPlansContext.Provider>
    );
};
